import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { globalConst, isEmptyArr } from 'modules/global';
import {
  ArticleCards,
  ArticleCategoryList,
  articleCons,
  getAllArticles,
  getAllNewsCategory,
  useAllNews
} from 'modules/news';

import { CustomPagination, EmptyData, NextHead, SocialMediaBanner, Visible } from 'components';

const Index: React.FC = () => {
  const t = useTranslations();

  const { locale = 'uz', query } = useRouter();
  const categoryId = query.category_id;
  const { news, isLoading } = useAllNews({
    page: query.page,
    languageId: globalConst.langIds[locale],
    categoryId
  });

  return (
    <>
      <NextHead title={t('meta.news.title')} description={t('meta.news.description')} />

      <div className="mx-auto my-3 min-h-screen max-w-screen md:my-7 md:p-4">
        <h1 className="mb-3 ml-3 text-left text-center text-xl font-bold md:mb-6 md:ml-0 md:text-2xl">
          {t('nav.yangiliklar')}
        </h1>

        <SocialMediaBanner />
        <div className="grid grid-cols-1 md:mt-5 md:grid-cols-4 md:gap-4">
          <div className="self-start md:sticky md:top-4">
            <ArticleCategoryList />
          </div>
          <div className="w-full space-y-4 sm:col-span-3">
            <Visible
              if={isLoading}
              else={
                <Visible
                  if={isEmptyArr(news?.data)}
                  else={news?.data?.map(article => (
                    <ArticleCards
                      key={article?.article_id}
                      title={article?.name}
                      desc={article?.description}
                      categoryTitle={article?.category_name}
                      viewCount={article?.views_count}
                      createdAt={article?.created_time}
                      slug={article?.slug}
                    />
                  ))}
                >
                  <EmptyData title={t('malumot_topilmadi')} />
                </Visible>
              }
            >
              {Array.from({ length: 10 }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="mb-4 flex flex-col gap-5 rounded-xl bg-white p-3 sm:flex-row">
                  <div className="sm:w-50 sm:min-w-50 relative h-60 w-full flex-shrink-0 sm:h-40 md:min-w-60">
                    <Skeleton.Image className="!h-full !w-full !rounded-xl" active />
                  </div>
                  <div className="flex-1">
                    <div>
                      <div>
                        <Skeleton.Input className="!w-full" active />
                      </div>
                      <div className=" mt-6 flex flex-col space-y-4">
                        <Skeleton.Input className="!h-3 !w-full" active />
                        <Skeleton.Input className="!w-100 !h-3" active />
                        <Skeleton.Input className="!w-70 !h-3" active />
                        <Skeleton.Input className="!h-3 !w-full" active />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Visible>
            {news?.data && (
              <div>
                <CustomPagination pageSize={10} total={news?.total} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const queryClient = new QueryClient();

  const languageId = globalConst.langIds[locale || 'uz'];

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [articleCons.queryKeys.ARTICLES, { languageId }],
      queryFn: getAllArticles({ languageId })
    }),
    queryClient.prefetchQuery({
      queryKey: [articleCons.queryKeys.ARTICLES_CATEGORY, languageId],
      queryFn: getAllNewsCategory(languageId)
    })
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      messages: (await import(`../../messages/${locale}.json`)).default
    },
    revalidate: 604800
  };
};
